import React, { useState, useEffect } from 'react';

import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { showToast } from '../utils/toast';
import { Loading } from '../components/Loading';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { User, ChangePasswordData } from '../types';
import { format } from 'date-fns';
import { IconNavProfile, IconShield, IconCheckCircle } from '../components/icons';

export const Profile: React.FC = () => {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
  });
  const [passwordData, setPasswordData] = useState<ChangePasswordData & { confirm_password: string }>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getProfile();
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData({
          username: response.data.username,
          email: response.data.email || '',
        });
      } else {
        showToast.error(response.message || 'Failed to load profile');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const updateData: any = {};
      if (formData.username !== profile?.username) {
        updateData.username = formData.username;
      }
      if (formData.email !== (profile?.email || '')) {
        updateData.email = formData.email || null;
      }

      if (Object.keys(updateData).length === 0) {
        showToast.warning('No changes to save');
        setSubmitting(false);
        return;
      }

      const response = await userService.updateProfile(updateData);
      if (response.success && response.data) {
        showToast.success('Profile updated successfully');
        setProfile(response.data);
        // Update auth context if username changed
        if (response.data.username !== authUser?.username) {
          window.location.reload();
        }
      } else {
        showToast.error(response.message || 'Failed to update profile');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      showToast.error('New password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await userService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      if (response.success) {
        showToast.success('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        } as ChangePasswordData & { confirm_password: string });
        setShowPasswordForm(false);
      } else {
        showToast.error(response.message || 'Failed to change password');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading fullScreen text="Loading profile..." />;
  }

  if (!profile) {
    return (
      <div className="p-6">
        <div className="text-red-600">Failed to load profile</div>
      </div>
    );
  }

  const getInitials = (name: string) => {
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Header Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-blue-600 to-blue-800"></div>
        <div className="px-6 pb-6">
          <div className="relative flex items-end -mt-12 mb-4">
            <div className="h-24 w-24 rounded-full border-4 border-white bg-white shadow-md flex items-center justify-center text-blue-700 text-3xl font-bold">
              {getInitials(profile.username)}
            </div>
            <div className="ml-4 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{profile.username}</h1>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span className="capitalize px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium border border-blue-100">
                  {profile.role}
                </span>
                <span>•</span>
                <span>Member since {format(new Date(profile.created_at), 'MMMM yyyy')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Personal Details - Left Column (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <IconNavProfile className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">User ID</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-mono text-sm">
                    #{profile.id}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Role</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 capitalize text-sm">
                    {profile.role}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Input
                    label="Username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email address"
                  />
                  <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                    <IconShield className="w-3 h-3" />
                    Used for notifications and account recovery
                  </p>
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Security Settings - Right Column (1/3 width) */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gray-50 rounded-lg text-gray-600">
                <IconShield className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Security</h2>
            </div>

            {!showPasswordForm ? (
              <div className="text-center py-6 space-y-4">
                <div className="mx-auto w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-green-600">
                  <IconCheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Password Active</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Your account is secured with a password. Update it periodically.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setShowPasswordForm(true)}
                >
                  Change Password
                </Button>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, current_password: e.target.value })
                  }
                  required
                  placeholder="••••••••"
                />

                <Input
                  label="New Password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, new_password: e.target.value })
                  }
                  required
                  minLength={6}
                  placeholder="Min 6 chars"
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirm_password: e.target.value })
                  }
                  required
                  minLength={6}
                  placeholder="Repeat new password"
                />

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordData({
                        current_password: '',
                        new_password: '',
                        confirm_password: '',
                      } as ChangePasswordData & { confirm_password: string });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    Update
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
